import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { medicalHubKeys } from '../medical/hooks/use-medical-hub';

import { AddTemperatureReadingBody, UpdateTemperatureReadingBody, temperatureApi } from './temperature';

export const temperatureKeys = {
  all: ['admin', 'temperature'] as const,
  roster: (classId: string, date: string) => ['admin', 'temperature', 'roster', classId, date] as const,
  student: (studentId: string) => ['admin', 'temperature', 'student', studentId] as const,
};

export function useTemperatureRosterQuery(classId: string | undefined, date: string | undefined) {
  return useQuery({
    queryKey: temperatureKeys.roster(classId ?? '', date ?? ''),
    queryFn: () => temperatureApi.roster(classId!, date!),
    enabled: Boolean(classId) && Boolean(date),
  });
}

export function useAddTemperatureReadingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AddTemperatureReadingBody) => temperatureApi.addReading(data),
    onSuccess: (_res, variables) => {
      void qc.invalidateQueries({
        queryKey: temperatureKeys.roster(variables.classId, variables.date),
      });
      // The nurse's board counts the round she is in the middle of.
      void qc.invalidateQueries({ queryKey: medicalHubKeys.todayAll });
      void qc.invalidateQueries({ queryKey: medicalHubKeys.temperatureAll });
    },
  });
}

export function useDeleteTemperatureReadingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; classId: string; date: string }) => temperatureApi.deleteReading(id),
    onSuccess: (_res, variables) => {
      void qc.invalidateQueries({
        queryKey: temperatureKeys.roster(variables.classId, variables.date),
      });
      // The nurse's board counts the round she is in the middle of.
      void qc.invalidateQueries({ queryKey: medicalHubKeys.todayAll });
      void qc.invalidateQueries({ queryKey: medicalHubKeys.temperatureAll });
    },
  });
}

export function useUpdateTemperatureReadingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTemperatureReadingBody; classId: string; date: string }) =>
      temperatureApi.updateReading(id, data),
    onSuccess: (_res, variables) => {
      void qc.invalidateQueries({
        queryKey: temperatureKeys.roster(variables.classId, variables.date),
      });
      // The nurse's board counts the round she is in the middle of.
      void qc.invalidateQueries({ queryKey: medicalHubKeys.todayAll });
      void qc.invalidateQueries({ queryKey: medicalHubKeys.temperatureAll });
    },
  });
}

export function useStudentTemperatureQuery(studentId: string | undefined) {
  return useQuery({
    queryKey: temperatureKeys.student(studentId ?? ''),
    queryFn: () => temperatureApi.listForStudent(studentId!),
    enabled: Boolean(studentId),
  });
}
